import { unsafeCSS, html } from 'lit';
import { state, property, queryAll } from 'lit/decorators.js';

import { PopBaseComponent } from '../../../base/pop-base-component.js';
import { Constants as ProductCenterTableWrapperConstants } from '../../product-center-table-wrapper/product-center-table-wrapper.constants.js';
import { Constants } from './esg-table.constants.js';
import {
  Product,
  SortDirection,
  TableRecordVariant,
} from '../../product-center-table-wrapper/product-center-table-wrapper.model.js';
import { getCurrencySymbol } from '../../../common/utils/currency.js';
import { buildShareClassDescriptor } from '../../../common/utils/buildShareClass.js';
import { zustandStateToComponent } from '../../store/product-center.store';
import { TableTypeEnum } from '../../store/product-center.store.model';
import {
  checkProductCallback,
  getReportButtonAriaLabel,
  handleCommonSortByDropdownChange,
  handleSortProductDataCells,
  handleUpdate,
  setAnalyticsAttributesForBeaconIconButton,
  renderProductCellData,
  renderProductHeader,
  renderReportButton,
  renderTableCells,
  renderTableHeader,
} from '../../product-center-table-wrapper/product-center-table-wrapper.utils.js';
import Locale from '../../../common/utils/locale.js';

import style from '../common/css/table.scss?inline';

/**
 * @tag pop-pc-esg-table-component
 * @property {ProductCenterExhibit} [componentObject] ESG Table content
 * @attribute {ProductCenterExhibit} [data-component-object] ESG Table content
 */
export class PopESGTableComponent extends PopBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  static readonly tagName = Constants.TAG_NAME;

  /**
   * @internal
   */
  @property()
  private filteredProducts: Product[] = [];

  /**
   * @internal
   */
  @property()
  private sortByDropdownValue =
    ProductCenterTableWrapperConstants.DEFAULT_SORT_BY_DROP_DOWN_OPTION.value;

  /**
   * @internal
   */
  @property()
  selectedProducts: Product['productCode'][] = [];

  @state()
  public sortDirectionState: SortDirection = SortDirection.ASCENDING;

  @property()
  tableHeaders: NodeListOf<Element> | undefined;

  @queryAll('beacon-icon-button.report-button')
  beaconIconButtons!: NodeListOf<HTMLElement>;

  constructor() {
    super();
    this.enableI18n = true;
  }

  /*
   * Component Methods
   */

  firstUpdated() {
    this.beaconIconButtons.forEach((button) => {
      setAnalyticsAttributesForBeaconIconButton(button);
    });
  }

  update(changedProperties: Map<string, unknown>) {
    handleUpdate(this, changedProperties);
    super.update(changedProperties);
  }

  private sortByDropdownChangeHandler(e: CustomEvent) {
    this.sortByDropdownValue = handleCommonSortByDropdownChange(
      e,
      ProductCenterTableWrapperConstants.DEFAULT_SORT_BY_DROP_DOWN_OPTION.value,
      this.tableHeaders,
      this.requestUpdate.bind(this),
    );
  }

  private sortProductDataCells(e: CustomEvent) {
    handleSortProductDataCells(this, e);
  }

  private renderTableHead() {
    return html`
      <tr>
        ${renderProductHeader(
          {
            rowspan: 1,
            sortByDropdownValue: this.sortByDropdownValue,
            sortByCallBack: this.sortByDropdownChangeHandler,
            onSort: this.sortProductDataCells,
            tableType: TableTypeEnum.ESG,
          },
          new Locale(this.language, this.country),
        )}
        ${renderTableHeader({
          columnId: Constants.ESG_COLUMN_IDS.PROXY_VOTING_SUMMARY,
          headerName: `${this.localize(Constants.TRANSLATION_KEYS.PROXY_VOTING_SUMMARY)}`,
          colspan: 1,
          rowspan: 2,
          parentHeader: null,
          sortable: false,
          customClass:
            ProductCenterTableWrapperConstants.CUSTOM_CLASSES.HEADER_ALIGNMENT,
        })}
        ${renderTableHeader({
          columnId: Constants.ESG_COLUMN_IDS.CARBON_FOOTPRINT_REPORT,
          headerName: `${this.localize(Constants.TRANSLATION_KEYS.CARBON_FOOTPRINT_REPORT)}`,
          colspan: 1,
          rowspan: 2,
          parentHeader: null,
          sortable: false,
          customClass:
            ProductCenterTableWrapperConstants.CUSTOM_CLASSES.HEADER_ALIGNMENT,
        })}
        ${renderTableHeader({
          columnId: Constants.ESG_COLUMN_IDS.ESG_ENGAGEMENT_REPORT,
          headerName: `${this.localize(Constants.TRANSLATION_KEYS.ESG_ENGAGEMENT_REPORT)}`,
          colspan: 1,
          rowspan: 2,
          parentHeader: null,
          sortable: false,
          customClass:
            ProductCenterTableWrapperConstants.CUSTOM_CLASSES.HEADER_ALIGNMENT,
        })}
        ${renderTableHeader({
          columnId: Constants.ESG_COLUMN_IDS.ANNUAL_IMPACT_REPORT,
          headerName: `${this.localize(Constants.TRANSLATION_KEYS.ANNUAL_IMPACT_REPORT)}`,
          colspan: 1,
          rowspan: 2,
          parentHeader: null,
          sortable: false,
          customClass:
            ProductCenterTableWrapperConstants.CUSTOM_CLASSES.HEADER_ALIGNMENT,
        })}
      </tr>
    `;
  }

  private renderTableBody() {
    return html`
      ${this.filteredProducts.map((product: Product) => {
        return html`
          <tr>
            ${renderProductCellData({
              index: JSON.stringify(product.productName),
              sortValue: product.productName[this.sortByDropdownValue] ?? '',
              checkboxLabel: product.productName.vehicleTypeName,
              fundName: product.productName.fundName,
              shareClass: product.productName.shareClass
                ? buildShareClassDescriptor(product.productName.shareClass)
                : '',
              currencySymbol: getCurrencySymbol(
                product.productName.baseCurrencyName,
              ),
              currencyName: product.productName.baseCurrencyName,
              apir: product.productName.apir ?? '',
              productCode: product.productCode,
              checkProductCallBack: (event: Event) =>
                checkProductCallback(event, TableTypeEnum.ESG),
              isChecked: this.selectedProducts.includes(product.productCode),
              fundLink: product.productName.fundLink,
              showPopCheckbox: false,
              disclaimers: product.disclaimers ?? undefined,
              apirLabel: this.localize(
                ProductCenterTableWrapperConstants.TRANSLATION_KEYS.APIR,
              ),
              noticeLabel: this.localize(
                ProductCenterTableWrapperConstants.TRANSLATION_KEYS.NOTICE,
              ),
            })}
            ${renderTableCells({
              sortValue: undefined,
              variant: TableRecordVariant.TEXT,
              cellClass: Constants.REPORT_ROW_CLASS,
              value: renderReportButton(
                product.esg?.proxyVotingSummary,
                getReportButtonAriaLabel(
                  product,
                  this.localize(
                    Constants.TRANSLATION_KEYS.PROXY_VOTING_SUMMARY,
                  ),
                ),
                this.localize(Constants.TRANSLATION_KEYS.PROXY_VOTING_SUMMARY),
              ),
            })}
            ${renderTableCells({
              sortValue: undefined,
              variant: TableRecordVariant.TEXT,
              cellClass: Constants.REPORT_ROW_CLASS,
              value: renderReportButton(
                product.esg?.carbonFootprintSummary,
                getReportButtonAriaLabel(
                  product,
                  this.localize(
                    Constants.TRANSLATION_KEYS.CARBON_FOOTPRINT_REPORT,
                  ),
                ),
                this.localize(
                  Constants.TRANSLATION_KEYS.CARBON_FOOTPRINT_REPORT,
                ),
              ),
            })}
            ${renderTableCells({
              sortValue: undefined,
              variant: TableRecordVariant.TEXT,
              cellClass: Constants.REPORT_ROW_CLASS,
              value: renderReportButton(
                product.esg?.esgEngagementReport,
                getReportButtonAriaLabel(
                  product,
                  this.localize(
                    Constants.TRANSLATION_KEYS.ESG_ENGAGEMENT_REPORT,
                  ),
                ),
                this.localize(Constants.TRANSLATION_KEYS.ESG_ENGAGEMENT_REPORT),
              ),
            })}
            ${renderTableCells({
              sortValue: undefined,
              variant: TableRecordVariant.TEXT,
              cellClass: Constants.REPORT_ROW_CLASS,
              value: renderReportButton(
                product.esg?.esgImpactAnnualReport,
                getReportButtonAriaLabel(
                  product,
                  this.localize(
                    Constants.TRANSLATION_KEYS.ANNUAL_IMPACT_REPORT,
                  ),
                ),
                this.localize(Constants.TRANSLATION_KEYS.ANNUAL_IMPACT_REPORT),
              ),
            })}
          </tr>
        `;
      })}
    `;
  }

  renderComponent() {
    return html` <beacon-table
      defer-until="visible"
      variant="fixed"
      density="standard"
    >
      <table>
        <thead>
          ${this.renderTableHead()}
        </thead>
        <tbody>
          ${this.renderTableBody()}
        </tbody>
      </table>
    </beacon-table>`;
  }

  render() {
    if (!this.filteredProducts && !this.componentObject) {
      return html``;
    }
    return this.renderComponent();
  }
}

customElements.define(
  Constants.TAG_NAME,
  zustandStateToComponent(
    {
      mapState: (productCenterState) => {
        return {
          filteredProducts: productCenterState.getFilteredProducts(),
          selectedProducts:
            productCenterState.selectedProducts[TableTypeEnum.ESG],
        };
      },
    },
    PopESGTableComponent,
  ),
);
